import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import hybridMappingService from '@/lib/hybridMappingService';
import { VEHICLE_RATES } from './constants';

export const usePriceCalculator = (formState) => {
  const { toast } = useToast();
  const { tripType, selectedPickup, selectedDropoff, vehicle, hours } = formState;
  
  const [distance, setDistance] = useState('');
  const [price, setPrice] = useState(0);
  const [details, setDetails] = useState('');

  useEffect(() => {
    const calculateDistance = async () => {
      if ((tripType === 'One Way' || tripType === 'Wait & Return') && selectedPickup && selectedDropoff) {
        try {
          // Validate coordinates before calculation
          if (!isValidCoordinate(selectedPickup) || !isValidCoordinate(selectedDropoff)) {
            setDistance('');
            return;
          }

          // Use hybrid mapping service to calculate distance
          const result = await hybridMappingService.calculateRouteDistance(
            selectedPickup,
            selectedDropoff
          );
          
          // Handle both number and object responses
          const miles = typeof result === 'number' ? result : result?.distance;
          
          // Validate distance result
          if (miles && miles > 0 && miles < 10000) { // Max 10,000 miles (reasonable limit)
            setDistance(miles.toFixed(1));
          } else {
            setDistance('');
          }
        } catch (error) {
          setDistance('');
          // Don't show error toast for routing failures - it's not critical
        }
      } else {
        setDistance('');
      }
    };
    calculateDistance();
  }, [selectedPickup, selectedDropoff, tripType, toast]);

  // Helper function to validate coordinates
  const isValidCoordinate = (coordinate) => {
    if (!coordinate || typeof coordinate.lat !== 'number' || typeof coordinate.lng !== 'number') {
      return false;
    }
    
    // Check latitude bounds (-90 to 90)
    if (coordinate.lat < -90 || coordinate.lat > 90) {
      return false;
    }
    
    // Check longitude bounds (-180 to 180)
    if (coordinate.lng < -180 || coordinate.lng > 180) {
      return false;
    }
    
    // Check for NaN or Infinity
    if (!isFinite(coordinate.lat) || !isFinite(coordinate.lng)) {
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    let calculatedPrice = 0;
    let priceDetails = '';
    
    // Removed hardcoded test - using real pricing logic
    
    // Trim any extra spaces and normalize the vehicle name
    const normalizedVehicle = vehicle.trim();
    const rate = VEHICLE_RATES[normalizedVehicle];

    // Price calculation logic

    if (!rate) {
      console.warn('❌ No rate found for vehicle:', normalizedVehicle);
      setPrice(0);
      setDetails('');
      return;
    }

    switch (tripType) {
      case 'One Way':
        // Check if this is an hourly-only vehicle
        const isHourlyOnly = normalizedVehicle === 'Stretch Limousine' || normalizedVehicle === 'Sprinter Van' || normalizedVehicle === 'Coach Bus';
        if (isHourlyOnly) {
          // For hourly-only vehicles, use hourly rate for One Way trips
          calculatedPrice = rate.hourly * 1; // Minimum 1 hour for One Way
          priceDetails = `$${rate.hourly}/hr (1 hour minimum)`;
        } else if (distance > 0 && rate.base !== undefined && rate.perMile !== undefined) {
          // Apply distance-based pricing rules for regular vehicles
          let perMileRate = rate.perMile;
          let distanceNote = '';
          
          if (distance > 15) {
            if (normalizedVehicle === 'Luxury SUV') {
              perMileRate = 3.25;
              distanceNote = ' (Long distance rate: $3.25/mile)';
            } else if (normalizedVehicle === 'Executive Sedan') {
              perMileRate = 2.75;
              distanceNote = ' (Long distance rate: $2.75/mile)';
            }
          }
          
          calculatedPrice = rate.base + (distance * perMileRate);
          priceDetails = `$${rate.base} base + $${perMileRate}/mile${distanceNote}`;
        } else {
          // No distance available - show base rate as minimum
          calculatedPrice = rate.base;
          priceDetails = `$${rate.base} base rate (minimum)`;
        }
        break;
      case 'Hourly':
      case 'As Directed':
        calculatedPrice = rate.hourly * hours;
        priceDetails = `$${rate.hourly}/hr for ${hours} hours`;
        if (tripType === 'As Directed') {
          priceDetails += ' (3hr min)';
        }
        break;
      case 'Wait & Return':
        // Check if this is an hourly-only vehicle
        const isHourlyOnlyWait = normalizedVehicle === 'Stretch Limousine' || normalizedVehicle === 'Sprinter Van' || normalizedVehicle === 'Coach Bus';
        
        if (isHourlyOnlyWait) {
          // For hourly-only vehicles, use hourly rate for Wait & Return trips
          calculatedPrice = rate.hourly * 2; // Minimum 2 hours for Wait & Return
          priceDetails = `$${rate.hourly}/hr (2 hour minimum for Wait & Return)`;
        } else if (distance > 0 && rate.base !== undefined && rate.perMile !== undefined) {
          // Apply distance-based pricing rules for regular vehicles
          let perMileRate = rate.perMile;
          let distanceNote = '';
          
          if (distance > 15) {
            if (normalizedVehicle === 'Luxury SUV') {
              perMileRate = 3.25;
              distanceNote = ' (Long distance rate: $3.25/mile)';
            } else if (normalizedVehicle === 'Executive Sedan') {
              perMileRate = 2.75;
              distanceNote = ' (Long distance rate: $2.75/mile)';
            }
          }
          
          const oneWayFare = rate.base + (distance * perMileRate);
          calculatedPrice = oneWayFare * 1.20; // 20% increase
          priceDetails = `One-way fare + 20%. Waiting extra.${distanceNote}`;
        }
        break;
      default:
        calculatedPrice = 0;
        priceDetails = '';
    }
    
    // Set the calculated price and details
    
    setPrice(calculatedPrice.toFixed(2));
    setDetails(priceDetails);
  }, [distance, vehicle, tripType, hours, selectedPickup, selectedDropoff]);

  return { price, details, distance };
};